import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { bookingActions } from '../../../_actions/booking.actions';
import BaileyAvatar, { BaileyStill } from '../../agent/bailey/BaileyAvatar';
import { PALETTES } from '../../shared/ColourPicker';
import { useOnboarding } from '../ImportJobContext';
import { setDemoTeam, tradeForBusinessType } from '../demoTeam';
import { salonBusinessType } from '../starterMenus';
import { hapticTick, hapticImpact, hapticSuccess } from '../../../_utils/haptics';
import agentService from '../../../_services/agent.service';
import {
  BAILEY_INTRO_ENABLED, INTRO_START_EVENT, getIntro, setIntro, markIntroDone,
  introColleagues, getLastRealStaffCount, INTRO_BOOKING_EVENT, introCheckout, introServiceCategories,
} from './introStage';
import { getPet, setPet as savePet } from './petStore';
import { SETUP_V2_ENABLED } from '../flags';
import { openSetup as openSetupV2 } from '../setup/SetupContext';
import { resolveAppearance } from '../../agent/bailey/lookStore';
import './BaileyIntro.css';

/**
 * Bailey's welcome: the first minute of Beu.
 *
 *   hero      Welcome to Beu, full screen
 *   meet      Bailey arrives centre stage and says hello
 *   team      Bailey flies to the corner, the diary is empty; two example
 *             colleagues appear with a poof
 *   fill      practice bookings cascade into every column
 *   custom    what the owner can change
 *   palette   they pick a palette and the diary recolours as they tap
 *   done      hand over to setup (the welcome fork)
 *
 * The diary underneath is the real one, reshaped in the browser by the
 * intro stage (introStage.js). Nothing is written, and leaving at any point
 * brings the real diary straight back.
 */

const TRADE_WORDS = {
  salon: { sidekick: 'salon', chair: 'behind the chair' },
  barber: { sidekick: 'barbershop', chair: 'behind the chair' },
  beauty: { sidekick: 'beauty', chair: 'with your clients' },
};

const HERO_CARDS = {
  salon: { client: 'Lacey Curls', service: 'Cut and Finish', price: '£48.00' },
  barber: { client: 'Will Barrow', service: 'Skin Fade', price: '£22.00' },
  beauty: { client: 'Rosie Cheeks', service: 'Lash Lift and Tint', price: '£45.00' },
};

// Bobbi's arrangement in the tour: self-employed on a 40% split, meaning the
// salon keeps 40% of what Bobbi takes and Bobbi keeps 60%.
const SALON_SPLIT = 0.4;
const pounds = (n) => `£${Number.isInteger(n) ? n : n.toFixed(2)}`;

const CUSTOMISE = ['Prices', 'Durations', 'Processing times', 'Deposits', 'Per team member', 'Colours'];

// The six lightest colours of a palette, the same ones the demo diary uses.
const lum = (hex) => {
  const n = parseInt(String(hex).replace('#', ''), 16);
  return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
};
const swatches = (p) => p.colours.map((c) => c.hex).sort((a, b) => lum(b) - lum(a)).slice(0, 6);

const reducedMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
};
const isPhone = () => {
  try { return window.matchMedia('(max-width: 767px)').matches; } catch (e) { return false; }
};
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
// Two frames, so a change has painted. Frames pause in a hidden tab, so a
// short timeout keeps the intro moving there too.
const nextFrame = () => new Promise((r) => {
  let done = false;
  const go = () => { if (!done) { done = true; r(); } };
  requestAnimationFrame(() => requestAnimationFrame(go));
  setTimeout(go, 120);
});
const bodyClass = (name, on) => document.body.classList[on ? 'add' : 'remove'](name);

/* ---------------- words that arrive one at a time ---------------- */

function Say({ title, body, onDone, instant }) {
  const words = useMemo(() => {
    const out = [];
    let i = 0;
    (title || '').split(' ').filter(Boolean).forEach((w) => { out.push({ w, i: i++, big: true }); });
    const titleCount = i;
    (body || '').split(' ').filter(Boolean).forEach((w) => { out.push({ w, i: i++, big: false }); });
    return { out, titleCount };
  }, [title, body]);

  useEffect(() => {
    const t = setTimeout(() => onDone && onDone(), instant ? 0 : Math.min(2400, words.out.length * 34 + 380));
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, body]);

  const span = ({ w, i }) => (
    <span key={i} className="bi-word" style={instant ? undefined : { animationDelay: `${i * 34}ms` }}>{w}{' '}</span>
  );
  return (
    <div className="bi-say" aria-live="polite">
      {title && <p className="bi-say-title">{words.out.slice(0, words.titleCount).map(span)}</p>}
      {body && <p className="bi-say-body">{words.out.slice(words.titleCount).map(span)}</p>}
    </div>
  );
}

/* ---------------- welcome to Beu ---------------- */

function Hero({ firstName, salonName, trade, leaving, onGo, onSkip }) {
  const ref = useRef(null);
  const card = HERO_CARDS[trade] || HERO_CARDS.salon;
  const words = TRADE_WORDS[trade] || TRADE_WORDS.salon;
  const goRef = useRef(null);

  // The floating cards drift with the pointer, a few pixels, so the page
  // feels alive under a mouse. Phones skip it.
  useEffect(() => {
    const el = ref.current;
    if (!el || reducedMotion()) return undefined;
    let raf = null;
    const move = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        el.style.setProperty('--px', ((e.clientX / window.innerWidth) - 0.5).toFixed(3));
        el.style.setProperty('--py', ((e.clientY / window.innerHeight) - 0.5).toFixed(3));
      });
    };
    window.addEventListener('pointermove', move);
    return () => { window.removeEventListener('pointermove', move); if (raf) cancelAnimationFrame(raf); };
  }, []);

  useEffect(() => { const t = setTimeout(() => goRef.current && goRef.current.focus({ preventScroll: true }), 1400); return () => clearTimeout(t); }, []);

  const title = ['Welcome', 'to'];
  return (
    <div className={`bi-hero${leaving ? ' is-leaving' : ''}`} ref={ref} role="dialog" aria-modal="true" aria-label="Welcome to Beu">
      <div className="bi-hero-bg" aria-hidden="true">
        <span className="bi-blob bi-blob--1" />
        <span className="bi-blob bi-blob--2" />
        <span className="bi-blob bi-blob--3" />
        <span className="bi-blob bi-blob--4" />
        <span className="bi-hero-grid" />
        <span className="bi-hero-grain" />
      </div>

      <button type="button" className="bi-skip bi-skip--light" onClick={onSkip}>Skip intro</button>

      <div className="bi-hero-cards" aria-hidden="true">
        <div className="bi-hc bi-hc--1" style={{ '--depth': 26, '--fx': '-40vw', '--fy': '-30vh' }}>
          <div className="bi-hc-in">
            <span className="bi-hc-strip" />
            <div><b>{card.client}</b><small>{card.service} · 10:30</small></div>
          </div>
        </div>
        <div className="bi-hc bi-hc--2" style={{ '--depth': 38, '--fx': '40vw', '--fy': '-30vh' }}>
          <div className="bi-hc-in">
            <span className="bi-hc-icon bi-hc-icon--teal">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
            </span>
            <div><b>Paid {card.price}</b><small>Card payment</small></div>
          </div>
        </div>
        <div className="bi-hc bi-hc--3" style={{ '--depth': 18, '--fx': '-40vw', '--fy': '30vh' }}>
          <div className="bi-hc-in">
            <span className="bi-hc-icon bi-hc-icon--violet">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 1112 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.9 1.9 0 003.4 0" /></svg>
            </span>
            <div><b>New online booking</b><small>Saturday 11:00</small></div>
          </div>
        </div>
        <div className="bi-hc bi-hc--4" style={{ '--depth': 32, '--fx': '40vw', '--fy': '30vh' }}>
          <div className="bi-hc-in">
            <span className="bi-hc-icon bi-hc-icon--peach">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12l18-8-8 18-2-8-8-2z" /></svg>
            </span>
            <div><b>Reminders sent</b><small>No-shows, handled</small></div>
          </div>
        </div>
      </div>

      <div className="bi-hero-inner">
        <img className="bi-hero-logo" src={require('../../../assets/images/beu-logo-white-2026.png')} alt="Beu" />
        <span className="bi-hero-eyebrow"><span className="bi-hero-dot" />{salonName ? `${salonName} is ready` : 'Your new workspace is ready'}</span>
        <h1 className="bi-hero-title">
          {title.map((w, i) => <span key={w} className="bi-hero-word" style={{ animationDelay: `${260 + i * 110}ms` }}>{w}</span>)}
          <span className="bi-hero-word bi-hero-word--brand" style={{ animationDelay: '480ms' }}>Beu</span>
        </h1>
        <p className="bi-hero-sub">
          {firstName ? `${firstName}, this is ` : 'This is '}the home for your bookings, payments, clients and marketing. Less time on admin, more time {words.chair}.
        </p>
        <button type="button" ref={goRef} className="bi-hero-cta" onClick={onGo}>
          <span>Let's go</span>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
        </button>
      </div>
    </div>
  );
}

/* ---------------- a puff of smoke, where a column appears ---------------- */

function poofAt(layer, x, y) {
  if (!layer || reducedMotion()) return;
  const frag = document.createDocumentFragment();
  const nodes = [];
  const add = (cls, vars) => {
    const n = document.createElement('span');
    n.className = cls;
    n.style.left = `${x}px`;
    n.style.top = `${y}px`;
    Object.keys(vars).forEach((k) => n.style.setProperty(k, vars[k]));
    frag.appendChild(n);
    nodes.push(n);
  };
  add('bi-ring', {});
  for (let i = 0; i < 12; i += 1) {
    const a = (i / 12) * Math.PI * 2 + Math.random() * 0.4;
    const r = 34 + Math.random() * 34;
    add('bi-puff', {
      '--dx': `${Math.cos(a) * r}px`, '--dy': `${Math.sin(a) * r * 0.75 - 8}px`,
      '--s': (1.1 + Math.random() * 0.9).toFixed(2), '--sz': `${18 + Math.round(Math.random() * 18)}px`,
      '--delay': `${Math.round(Math.random() * 60)}ms`, '--dur': `${620 + Math.round(Math.random() * 280)}ms`,
    });
  }
  for (let i = 0; i < 7; i += 1) {
    const a = (i / 7) * Math.PI * 2 + 0.3;
    const r = 58 + Math.random() * 30;
    add(`bi-spark${i % 2 ? ' bi-spark--gold' : ''}`, {
      '--dx': `${Math.cos(a) * r}px`, '--dy': `${Math.sin(a) * r}px`,
      '--delay': `${90 + Math.round(Math.random() * 90)}ms`, '--rot': `${Math.round(Math.random() * 180)}deg`,
    });
  }
  layer.appendChild(frag);
  setTimeout(() => nodes.forEach((n) => n.remove()), 1400);
}

/* ---------------- stagger, so bookings cascade down the day ---------------- */

function staggerBlocks(spread) {
  const blocks = Array.from(document.querySelectorAll('.dv2-staff-column .dv2-event-block'));
  const columns = Array.from(document.querySelectorAll('.dv2-staff-column'));
  const vh = window.innerHeight;
  let max = 0;
  blocks.forEach((b) => {
    const col = columns.indexOf(b.closest('.dv2-staff-column'));
    const top = b.getBoundingClientRect().top;
    const down = Math.max(0, Math.min(1, (top - 60) / (vh - 120)));
    const d = Math.round(Math.max(0, col) * spread * 0.12 + down * spread);
    b.style.setProperty('--bi-d', `${d}ms`);
    if (top < vh && top > -40) max = Math.max(max, d);
  });
  return max;
}

/* ---------------- the palette sheet ---------------- */

function PaletteSheet({ current, onPick, onDone, sheetRef }) {
  return (
    <div className="bi-palette" ref={sheetRef} role="dialog" aria-label="Pick your palette">
      <div className="bi-palette-head">
        <div>
          <h3>Pick your palette</h3>
          <p>Tap one to try it on your diary.</p>
        </div>
      </div>
      <div className="bi-palette-grid" role="radiogroup" aria-label="Palettes">
        {PALETTES.map((p) => {
          const on = p.name === current;
          return (
            <button key={p.name} type="button" role="radio" aria-checked={on} className={`bi-pal${on ? ' is-on' : ''}`} onClick={() => onPick(p.name)}>
              <span className="bi-pal-dots" aria-hidden="true">
                {swatches(p).map((hex, i) => <span key={hex + i} style={{ background: hex, zIndex: 6 - i }} />)}
              </span>
              <span className="bi-pal-name">{p.name}</span>
              {on && (
                <span className="bi-pal-tick" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="bi-actions is-in bi-palette-foot">
        <button type="button" className="bi-btn bi-btn--primary" onClick={onDone}>Looks good</button>
      </div>
    </div>
  );
}

/* ---------------- the conductor ---------------- */

const DOCKED_STEPS = ['team', 'teamDone', 'fill', 'custom', 'paletteAsk', 'bookAsk', 'bookClient', 'bookService', 'bookSave', 'bookSaved', 'mixTeam', 'mixPick', 'coSummary', 'coSplit', 'coReader', 'coPartial', 'done'];
const CHECKOUT_STEPS = ['coSummary', 'coSplit', 'coReader', 'coPartial'];
const BOOKING_STEPS = ['bookAsk', 'bookClient', 'bookService', 'bookSave'];
const HIGHLIGHTS = {
  bookClient: 'bi-hl-client', bookService: 'bi-hl-service', bookSave: 'bi-hl-save',
  coSummary: 'bi-hl-pay', coSplit: 'bi-hl-split', coReader: 'bi-hl-reader', coPartial: 'bi-hl-partial',
};
// The control each step points at, scrolled into view inside the panel.
const STEP_TARGETS = {
  bookClient: '.nbp-select-client-btn', bookService: '.bdp-edit-add-btn--below',
  coSplit: '.co-split', coReader: '.co-payment-tile', coPartial: '.co-amount-option:nth-child(2)',
};
const BODY_CLASSES = ['bi-coach-top', 'bi-running', 'bi-poofing', 'bi-fill-pending', 'bi-fill-go', 'bi-recolour', 'bi-clearing', 'bi-hl-client', 'bi-hl-service', 'bi-hl-save', 'bi-spotlight', 'bi-hl-pay', 'bi-hl-split', 'bi-hl-reader', 'bi-hl-partial'];

// Booking blocks carry "Client - Service" as their title.
const blockFor = (prefix, within) => Array.from((within || document).querySelectorAll('.dv2-event--booking'))
  .find((x) => (x.getAttribute('title') || '').indexOf(prefix) === 0) || null;

// Scroll a diary block up to just under the staff headers when the edge of
// the screen, or Bailey's card (the lower half of a phone), would hide it.
// The element that scrolls the diary, found from anything inside it.
function diaryScroller(fromEl) {
  let el = fromEl && fromEl.parentElement;
  while (el && el !== document.body) {
    const oy = getComputedStyle(el).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) return el;
    el = el.parentElement;
  }
  return null;
}
function scrollDiary(fromEl, delta) {
  const scroller = diaryScroller(fromEl);
  if (scroller) scroller.scrollBy({ top: delta, behavior: 'smooth' });
  else window.scrollBy({ top: delta, behavior: 'smooth' });
}

async function bringIntoView(el, room = 16) {
  const r0 = el.getBoundingClientRect();
  const head = document.querySelector('.dv2-staff-header');
  const headBottom = head ? head.getBoundingClientRect().bottom : 80;
  const coverLine = window.innerHeight * (isPhone() ? 0.42 : 0.8);
  if (r0.top >= headBottom + room && r0.bottom <= coverLine) return;
  scrollDiary(el, r0.top - (headBottom + room));
  await wait(450);
}

/* ---------------- reading the diary's clock off the page ---------------- */

// The time axis labels ("11:00") give minutes to screen position, so the
// intro can point at a time on the real diary whatever the zoom or scroll.
function hourMarks() {
  return Array.from(document.querySelectorAll('.dv2-time-label'))
    .map((el) => {
      const m = /^(\d{1,2}):(\d{2})$/.exec((el.textContent || '').trim());
      if (!m) return null;
      const r = el.getBoundingClientRect();
      return { minutes: Number(m[1]) * 60 + Number(m[2]), y: r.top + r.height / 2 };
    })
    .filter(Boolean)
    .sort((a, b) => a.minutes - b.minutes);
}
// The first whole hour showing below the staff headers.
function firstVisibleMinutes() {
  const head = document.querySelector('.dv2-staff-header');
  const top = head ? head.getBoundingClientRect().bottom : 0;
  const mark = hourMarks().find((m) => m.y >= top + 10);
  return mark ? mark.minutes : 10 * 60;
}
// "Al Fresco's", "Bea Keeper's"; a name ending in s takes a bare apostrophe.
const possessive = (name) => {
  const n = String(name || '').trim();
  // A walk-in has no name to own the booking: "the walk-in's"
  if (!n || /^walk-?in$/i.test(n)) return "the walk-in's";
  return /s$/i.test(n) ? `${n}'` : `${n}'s`;
};
const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

// The diary mounts more than once on its first load, so "the intro is
// running" lives in the session, not only in component state. A reload
// mid-intro starts it again from the top rather than stranding the owner
// on an empty diary.
const RUN_KEY = 'beu:bailey-intro-run';
const readRun = () => { try { return JSON.parse(sessionStorage.getItem(RUN_KEY)); } catch (e) { return null; } };
const writeRun = (v) => { try { if (v) sessionStorage.setItem(RUN_KEY, JSON.stringify(v)); else sessionStorage.removeItem(RUN_KEY); } catch (e) { /* ignore */ } };

export default function BaileyIntro() {
  const user = useSelector((s) => s.user) || {};
  const dispatch = useDispatch();
  const draftOpen = useSelector((s) => !!(s.minimisedBooking && s.minimisedBooking.active));
  const { openLayer, patchOnboarding, onboarding } = useOnboarding() || {};
  const [run, setRun] = useState(null);           // { handoff } while running
  const [step, setStep] = useState('hero');
  const [heroLeaving, setHeroLeaving] = useState(false);
  const [pet, setPet] = useState('hidden');        // hidden | arrive | centre | sliding | docked | leaving
  const [bailey, setBailey] = useState({ state: 'offstage', activity: 'none' });
  const [climbing, setClimbing] = useState(false); // Bailey is hauling himself onto the ledge
  const [ready, setReady] = useState(false);       // the words have arrived; show the buttons
  const [busy, setBusy] = useState(false);
  const [finishing, setFinishing] = useState(false); // the tour is packing up
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [palette, setPalette] = useState(getIntro().palette);
  const [look, setLook] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false); // the practice booking panel
  const [booked, setBooked] = useState(null);        // the practice booking, once saved
  const [retry, setRetry] = useState(false);         // closed the panel without saving
  const [pickedPalette, setPickedPalette] = useState(false);
  const [spot, setSpot] = useState(null);             // the booking in the spotlight
  const chipRef = useRef(null);
  const closingCheckoutRef = useRef(false); // Bailey closed checkout himself
  const [payLabel, setPayLabel] = useState(''); // what the checkout's Pay button says right now
  const targetRef = useRef(null);
  const fxRef = useRef(null);
  const petRef = useRef(null);
  const panelRef = useRef(null);
  const ctaRef = useRef(null);
  const ledgeRef = useRef(null);

  const info = user.userInfo || {};
  const firstName = String(info.name || '').trim().split(' ')[0] || '';
  const salonName = (info.hairdresser && info.hairdresser.salon && info.hairdresser.salon.salonName) || '';
  const trade = tradeForBusinessType(salonBusinessType(user));
  const words = TRADE_WORDS[trade] || TRADE_WORDS.salon;
  const [colleagueA, colleagueB] = introColleagues(trade);

  /* ----- start and stop ----- */

  const start = useCallback((opts) => {
    if (!BAILEY_INTRO_ENABLED) return;
    const next = { handoff: !!(opts && opts.handoff) };
    // A booking draft left open (it survives reloads) would sit over the
    // tour and save for real, so it is put away first.
    if (draftOpen) dispatch(bookingActions.closeMinimisedBooking());
    writeRun(next);
    setRun(next);
    setStep('hero');
    setHeroLeaving(false);
    setPet('hidden');
    setReady(false);
    setBusy(false);
    setFinishing(false);
    setPaletteOpen(false);
    setClimbing(false);
    setPanelOpen(false);
    setBooked(null);
    setRetry(false);
    setPickedPalette(false);
    setSpot(null);
    setBailey({ state: 'offstage', activity: 'none' });
    setIntro({ stage: 'blank', trade, palette: getIntro().palette || 'Coastal' });
    bodyClass('bi-running', true);
  }, [trade, draftOpen, dispatch]);

  // A stage left behind by a reload or a closed tab must never keep a real
  // diary looking empty, so anything stale is cleared on arrival.
  useEffect(() => {
    if (!BAILEY_INTRO_ENABLED) return undefined;
    const params = new URLSearchParams(window.location.search);
    if (params.get('intro') === '1') {
      params.delete('intro');
      const q = params.toString();
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${q ? `?${q}` : ''}`);
      start({ handoff: false });
    } else if (readRun()) {
      start(readRun());
    } else if (getIntro().stage !== 'off') {
      setIntro({ stage: 'off' });
    }
    const onStart = (e) => start((e && e.detail) || {});
    window.addEventListener(INTRO_START_EVENT, onStart);
    return () => {
      window.removeEventListener(INTRO_START_EVENT, onStart);
      // Leaving the diary mid-intro: give the real diary back. A remount
      // picks the intro up again from the session flag.
      if (getIntro().stage !== 'off') setIntro({ stage: 'off' });
      BODY_CLASSES.forEach((c) => bodyClass(c, false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // choice: 'setup' (they want help setting up), 'later' (not now), or
  // nothing (Skip), which carries on to setup for a new signup as before.
  const finish = useCallback(async (choice) => {
    if (!run) return;
    const { handoff } = run;
    const picked = typeof choice === 'string' ? choice : null;
    const openSetup = picked === 'setup' || (!picked && handoff);
    setBusy(true);
    setFinishing(true);
    setPaletteOpen(false);
    // The desk pet takes over from where Bailey is standing now, unless he
    // has been put somewhere before.
    if (petRef.current && getPet().x == null) {
      const r = petRef.current.querySelector('.bi-pet-y').getBoundingClientRect();
      if (r.width) savePet({ x: Math.round(r.left), y: Math.round(r.top), visible: true });
    }
    markIntroDone();
    writeRun(null);
    // Any booking panel or checkout still open is a practice one; it goes
    // with the tour.
    if (draftOpen) dispatch(bookingActions.closeMinimisedBooking());
    closingCheckoutRef.current = true;
    introCheckout('close');
    const { trade: t, palette: p } = getIntro();
    const newSalon = getLastRealStaffCount() <= 1;
    // The owner's practice bookings leave first, then Bailey.
    bodyClass('bi-clearing', true);
    setPet((cur) => (cur === 'hidden' ? cur : 'leaving'));
    await wait(reducedMotion() ? 0 : 420);
    setIntro({ stage: 'off' });
    if (newSalon) setDemoTeam(true, t, p);
    BODY_CLASSES.forEach((c) => bodyClass(c, false));
    setRun(null);
    setPet('hidden');
    setPanelOpen(false);
    setBooked(null);
    // Remembered on the server too, so signing out or another device never
    // replays the welcome or leaves it stranded part-way.
    if (patchOnboarding) patchOnboarding({ checklist: { welcomeDone: true } });
    if (SETUP_V2_ENABLED) {
      // Set up with Bailey carries on from here. "Not now" switches nothing
      // off: the plan waits with Bailey until they come back to it.
      if (openSetup) setTimeout(() => openSetupV2({ from: 'welcome' }), 180);
      else if (picked === 'later') openSetupV2({ park: true });
      return;
    }
    if (openSetup && openLayer) setTimeout(() => openLayer({ mode: 'setup' }), 180);
    // "Not now" on a new signup is the same as "I'll explore on my own".
    if (picked === 'later' && handoff && patchOnboarding) {
      patchOnboarding({ status: onboarding && onboarding.status === 'completed' ? 'completed' : 'dismissed' });
    }
  }, [run, openLayer, patchOnboarding, onboarding, draftOpen, dispatch]);

  /* ----- where Bailey stands: centre stage or docked in the corner ----- */

  useLayoutEffect(() => {
    const el = petRef.current;
    if (!el) return undefined;
    const place = () => {
      const phone = isPhone();
      // Laid out at centre-stage size, scaled down when docked, so he is
      // crisp and big on the ledge and the same pet size in the corner.
      const w = phone ? 190 : 260;
      const h = phone ? 209 : 286;
      const right = phone ? 12 : 26;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const k = phone ? 0.41 : 0.46;
      const bubble = panelRef.current;
      const panelH = bubble ? bubble.getBoundingClientRect().height : 0;
      // On a phone the booking panel fills the screen. Bailey hops into his
      // card as a small face, the card goes to the very top, and the panel
      // gives up exactly that much height (bi-coach-top), so the card never
      // sits over the panel's header, fields or Save.
      if (phone && bubble && bubble.classList.contains('bi-bubble')) {
        if (panelOpen) {
          bubble.style.top = 'calc(8px + env(safe-area-inset-top, 0px))';
          bubble.style.bottom = 'auto';
          document.documentElement.style.setProperty('--bi-coach-h', `${Math.round(panelH)}px`);
        } else {
          bubble.style.top = '';
          bubble.style.bottom = '';
        }
      }
      bodyClass('bi-coach-top', !!(phone && panelOpen && bubble && bubble.classList.contains('bi-bubble')));
      const bottom = phone ? Math.max(24, 10 + panelH + 4) : 92;
      // Desktop, panel open: Bailey runs to the left of the diary so the
      // panel stays clear, and talks from there.
      if (!phone) {
        const diary = document.querySelector('.dv2-container');
        const leftEdge = (diary ? diary.getBoundingClientRect().left : 0) + 18;
        el.style.setProperty('--bi-lx', `${leftEdge - (vw - right - w * k)}px`);
        el.style.setProperty('--bi-drop', `${bottom - 12}px`);
        if (bubble && bubble.classList.contains('bi-bubble')) {
          if (pet === 'left' || pet === 'run-left') {
            const sheet = document.querySelector('.sp-sheet');
            const limit = (sheet ? sheet.getBoundingClientRect().left : vw) - 16;
            const left = leftEdge + w * k + 14;
            bubble.style.left = `${left}px`;
            bubble.style.right = 'auto';
            bubble.style.bottom = '26px';
            bubble.style.width = `${Math.max(240, Math.min(380, limit - left))}px`;
          } else {
            bubble.style.left = '';
            bubble.style.right = '';
            bubble.style.bottom = '';
            bubble.style.width = '';
          }
        }
      }
      // The ledge: the top edge of the block the words sit on. Bailey's floor
      // is drawn 8% up from the bottom of his box, so the box is placed to put
      // that floor exactly on the ledge.
      const ledge = Math.round(vh * (phone ? 0.47 : 0.5));
      if (ledgeRef.current) ledgeRef.current.style.top = `${ledge}px`;
      el.style.setProperty('--bi-w', `${w}px`);
      el.style.setProperty('--bi-h', `${h}px`);
      el.style.setProperty('--bi-r', `${right}px`);
      el.style.setProperty('--bi-b', `${bottom}px`);
      el.style.setProperty('--bi-k', String(k));
      el.style.setProperty('--bi-cx', `${-(vw / 2) + w / 2 + right}px`);
      el.style.setProperty('--bi-cy', `${ledge + h * 0.08 - vh + bottom}px`);
    };
    place();
    // The booking panel slides in; measure again once it has settled.
    const later = panelOpen ? setTimeout(place, 450) : null;
    window.addEventListener('resize', place);
    const ro = typeof ResizeObserver !== 'undefined' && panelRef.current ? new ResizeObserver(place) : null;
    if (ro) ro.observe(panelRef.current);
    return () => { window.removeEventListener('resize', place); if (ro) ro.disconnect(); if (later) clearTimeout(later); };
  }, [run, step, paletteOpen, pet, panelOpen]);

  /* ----- the beats ----- */

  const say = useCallback((next, baileyNext) => {
    setReady(false);
    setStep(next);
    if (baileyNext) setBailey(baileyNext);
  }, []);

  const heroGo = async () => {
    hapticTick();
    setHeroLeaving(true);
    // Bailey is already in place, below the ledge and out of sight.
    setPet('arrive');
    await wait(reducedMotion() ? 0 : 650);
    setPet('centre');
    if (reducedMotion()) {
      say('meet', { state: 'attentive', activity: 'none' });
      return;
    }
    setClimbing(true);
    say('meet', { state: 'climb', activity: 'none' });
    await wait(3000);
    hapticImpact();
    await wait(750);
    setClimbing(false);
    setBailey({ state: 'complete', activity: 'none' });
    setTimeout(() => setBailey({ state: 'attentive', activity: 'none' }), 1500);
  };

  // Down and away: a hop to get going, a slide that picks up speed towards
  // the corner, shrinking as he goes, then a little bump as he lands.
  const flyToCorner = async () => {
    hapticTick();
    setReady(false);
    setStep('fly');
    if (reducedMotion()) {
      setPet('docked');
      say('team', { state: 'idle', activity: 'none' });
      return;
    }
    setBailey({ state: 'slide', activity: 'none' });
    setPet('sliding');
    await wait(1250);
    setPet('docked');
    hapticTick();
    setBailey({ state: 'complete', activity: 'none' });
    say('team');
    setTimeout(() => setBailey((b) => (b.state === 'complete' ? { state: 'idle', activity: 'none' } : b)), 1400);
  };

  const addTeam = async () => {
    hapticTick();
    setBusy(true);
    setReady(false);
    setBailey({ state: 'working', activity: 'laptop' });
    await wait(reducedMotion() ? 0 : 750);
    bodyClass('bi-poofing', true);
    setIntro({ stage: 'team' });
    await nextFrame();
    // The new columns start at zero width and grow, so the poof aims at
    // where each header will land, timed with the header popping in.
    const all = Array.from(document.querySelectorAll('.dv2-staff-column'));
    const first = all[0];
    if (first) {
      const fr = first.getBoundingClientRect();
      const box = first.parentElement.getBoundingClientRect();
      const visibleRight = Math.min(box.right, window.innerWidth);
      const w = Math.max(isPhone() ? 100 : 150, (visibleRight - fr.left) / all.length);
      const head = first.querySelector('.dv2-staff-header');
      const hr = head ? head.getBoundingClientRect() : fr;
      const y = hr.top + Math.min(hr.height, 110) / 2;
      all.forEach((c, idx) => {
        if (!c.classList.contains('dv2-staff-column--demo')) return;
        const order = all.filter((x, j) => j < idx && x.classList.contains('dv2-staff-column--demo')).length;
        setTimeout(() => { poofAt(fxRef.current, fr.left + w * (idx + 0.5), y); hapticImpact(); }, 300 + order * 280);
      });
    }
    await wait(reducedMotion() ? 0 : 1400);
    bodyClass('bi-poofing', false);
    setBusy(false);
    say('teamDone', { state: 'complete', activity: 'none' });
  };

  const fillDiary = async () => {
    hapticTick();
    setBusy(true);
    setReady(false);
    setBailey({ state: 'working', activity: 'diary' });
    await wait(reducedMotion() ? 0 : 700);
    bodyClass('bi-fill-pending', true);
    // Leave a clear stretch in the first colleague's column, in the part of
    // the day already on screen, for the booking Bailey asks for later.
    const from = Math.min(15 * 60, Math.max(9 * 60 + 30, firstVisibleMinutes() + 30));
    setIntro({ stage: 'fill', gap: { from, to: from + 150 }, added: [] });
    await nextFrame();
    const last = staggerBlocks(1100);
    bodyClass('bi-fill-go', true);
    bodyClass('bi-fill-pending', false);
    await wait(reducedMotion() ? 200 : last + 900);
    bodyClass('bi-fill-go', false);
    hapticSuccess();
    setBusy(false);
    say('custom', { state: 'complete', activity: 'none' });
  };

  const openPalette = () => {
    hapticTick();
    staggerBlocks(520);
    bodyClass('bi-recolour', true);
    setPaletteOpen(true);
    setPickedPalette(true);
    setBailey({ state: 'attentive', activity: 'none' });
  };

  const pickPalette = (name) => {
    if (name === palette) return;
    hapticTick();
    staggerBlocks(520);
    setPalette(name);
    setIntro({ palette: name });
    setBailey({ state: 'complete', activity: 'none' });
    setTimeout(() => setBailey({ state: 'attentive', activity: 'none' }), 900);
  };

  const paletteDone = () => {
    hapticSuccess();
    setPaletteOpen(false);
    setTimeout(() => bodyClass('bi-recolour', false), 800);
    setRetry(false);
    say('bookAsk', { state: 'complete', activity: 'none' });
  };

  /* ----- the practice booking ----- */

  // Desktop: hop down to the bottom and run left, clear of the panel.
  const runTo = useCallback(async (side) => {
    if (isPhone() || reducedMotion()) { setPet(side === 'left' ? (isPhone() ? 'docked' : 'left') : 'docked'); return; }
    setBailey({ state: side === 'left' ? 'run-left' : 'run-right', activity: 'none' });
    setPet(side === 'left' ? 'run-left' : 'run-right');
    await wait(1300);
    setPet(side === 'left' ? 'left' : 'docked');
    setBailey({ state: 'attentive', activity: 'none' });
  }, []);

  // A new practice booking lands: find it on the diary and celebrate it.
  const celebrate = useCallback(async (b) => {
    for (let i = 0; i < 20; i += 1) {
      await wait(60);
      const el = Array.from(document.querySelectorAll('.dv2-event-block'))
        .find((x) => (x.getAttribute('title') || '').indexOf(`${b.clientName} - `) === 0);
      if (el) {
        await bringIntoView(el);
        el.classList.add('bi-new-booking');
        const r = el.getBoundingClientRect();
        poofAt(fxRef.current, r.left + r.width / 2, r.top + Math.min(r.height, 80) / 2);
        hapticSuccess();
        setTimeout(() => el.classList.remove('bi-new-booking'), 1600);
        return;
      }
    }
  }, []);

  /* ----- mixed teams: the spotlight on a self-employed booking ----- */

  // The booking they just made when it is Bobbi's; otherwise the first of
  // Bobbi's own, so the story holds whichever column they tapped.
  const showSpot = useCallback(async () => {
    hapticTick();
    const col = document.querySelector('.dv2-staff-column--demo');
    const added = (getIntro().added || []).slice(-1)[0];
    let next = null;
    const priceOf = (names) => {
      const list = (introServiceCategories()[0] || {}).hairdresserService || [];
      return names.reduce((sum, n) => sum + (((list.find((x) => x.serviceName === n)) || {}).servicePrice || 0), 0);
    };
    if (added && added.staffId === 'demo-staff-1' && col && blockFor(`${added.clientName} - `, col)) {
      next = { prefix: `${added.clientName} - `, client: added.clientName, service: (added.services[0] || {}).name || 'booking', ours: true,
        price: (added.services || []).reduce((sum, x) => sum + (Number(x.price) || 0), 0) };
    } else if (col) {
      const el = col.querySelector('.dv2-event--booking');
      const [client, service] = String((el && el.getAttribute('title')) || '').split(' - ');
      if (client) next = { prefix: `${client} - `, client, service: service || 'booking', ours: false, price: priceOf(String(service || '').split(', ')) };
    }
    if (!next) { say('done', { state: 'complete', activity: 'none' }); return; }
    setSpot(next);
    const el = blockFor(next.prefix, col);
    // Room above it for the "Tap to open" chip.
    if (el) await bringIntoView(el, 52);
    say('mixPick', { state: 'attentive', activity: 'none' });
  }, [say]);

  // Dim the diary and keep the spotlit booking lit, with a chip above it,
  // for as long as Bailey is asking them to tap it.
  useEffect(() => {
    if (!run || step !== 'mixPick' || !spot) return undefined;
    bodyClass('bi-spotlight', true);
    let raf = null;
    let lit = null;
    const tick = () => {
      const el = blockFor(spot.prefix, document.querySelector('.dv2-staff-column--demo'));
      if (el !== lit) { if (lit) lit.classList.remove('bi-spotlit'); lit = el; }
      if (el && !el.classList.contains('bi-spotlit')) el.classList.add('bi-spotlit');
      const chip = chipRef.current;
      if (chip && el) {
        const r = el.getBoundingClientRect();
        const head = document.querySelector('.dv2-staff-header');
        const headBottom = head ? head.getBoundingClientRect().bottom : 60;
        // Above the booking, or below it when the headers are in the way.
        const above = r.top - 44 >= headBottom;
        chip.style.left = `${r.left + r.width / 2}px`;
        chip.style.top = above ? `${r.top - 10}px` : `${r.bottom + 10}px`;
        chip.style.transform = above ? 'translate(-50%, -100%)' : 'translate(-50%, 0)';
        chip.style.opacity = r.bottom > headBottom && r.top < window.innerHeight ? '1' : '0';
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (lit) lit.classList.remove('bi-spotlit');
      document.querySelectorAll('.bi-spotlit').forEach((x) => x.classList.remove('bi-spotlit'));
      bodyClass('bi-spotlight', false);
    };
  }, [run, step, spot]);

  // Bailey quotes the checkout's own Pay button, so adding a product or
  // changing a quantity keeps what he says and what it says the same.
  useEffect(() => {
    if (!run || step !== 'coSummary') return undefined;
    const read = () => {
      const btn = document.querySelector('.co-panel .bdp-footer .bdp-btn--primary');
      const text = btn ? btn.innerText.trim() : '';
      setPayLabel((cur) => (/^Pay /.test(text) && text !== cur ? text : cur));
    };
    read();
    const t = setInterval(read, 400);
    return () => clearInterval(t);
  }, [run, step]);

  // The practice panel reports each step; Bailey follows along.
  useEffect(() => {
    if (!run) return undefined;
    const on = async (e) => {
      const d = (e && e.detail) || {};
      if (d.step === 'opened') {
        setPanelOpen(true);
        say('bookClient', { state: 'attentive', activity: 'none' });
        runTo('left');
      } else if (d.step === 'client') {
        say('bookService', { state: 'complete', activity: 'none' });
      } else if (d.step === 'service') {
        say('bookSave', { state: 'complete', activity: 'none' });
      } else if (d.step === 'saved') {
        setPanelOpen(false);
        setBooked(d);
        setReady(false);
        await wait(350);
        // Bailey stays on the left: the checkout panel is next, on the right.
        await celebrate(d);
        say('bookSaved', { state: 'complete', activity: 'none' });
      } else if (d.step === 'bookingTapped') {
        // Only the spotlit booking moves the story on: it opens checkout.
        if (step !== 'mixPick' || !spot || String(d.title || '').indexOf(spot.prefix) !== 0) return;
        hapticSuccess();
        introCheckout('open', d.bookingId);
      } else if (d.step === 'checkoutOpened') {
        setPanelOpen(true);
        say('coSummary', { state: 'attentive', activity: 'none' });
      } else if (d.step === 'checkoutPayment') {
        if (step === 'coSummary') say('coSplit', { state: 'complete', activity: 'none' });
      } else if (d.step === 'checkoutScreen') {
        // Back to the summary from the payment screen: pick up from Pay.
        if (d.screen === 'summary' && ['coSplit', 'coReader', 'coPartial'].includes(step)) say('coSummary', { state: 'attentive', activity: 'none' });
      } else if (d.step === 'checkoutClosed') {
        setPanelOpen(false);
        // Closed by the owner part-way: back to tapping the booking. Closed
        // by Bailey at the end of the lesson: carry on.
        if (closingCheckoutRef.current) { closingCheckoutRef.current = false; return; }
        if (CHECKOUT_STEPS.includes(step)) say('mixPick', { state: 'attentive', activity: 'none' });
      } else if (d.step === 'closed') {
        setPanelOpen(false);
        setRetry(true);
        say('bookAsk', { state: 'attentive', activity: 'none' });
      }
    };
    window.addEventListener(INTRO_BOOKING_EVENT, on);
    return () => window.removeEventListener(INTRO_BOOKING_EVENT, on);
  }, [run, runTo, celebrate, say, step, spot]);

  // Pulse the control Bailey is talking about, and scroll the panel to it
  // when it is out of view (a phone panel is shorter while Bailey's card
  // sits above it).
  useEffect(() => {
    Object.values(HIGHLIGHTS).forEach((c) => bodyClass(c, false));
    if (!run || !HIGHLIGHTS[step]) return undefined;
    bodyClass(HIGHLIGHTS[step], true);
    const target = STEP_TARGETS[step];
    const t = target ? setTimeout(() => {
      const el = document.querySelector(`.sp-sheet ${target}`);
      if (!el) return;
      const r = el.getBoundingClientRect();
      const footer = document.querySelector('.sp-sheet .bdp-footer');
      const limit = footer ? footer.getBoundingClientRect().top : window.innerHeight;
      if (r.bottom > limit - 8 || r.top < 0) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 500) : null;
    return () => { if (t) clearTimeout(t); };
  }, [run, step]);

  // The glowing target on the free stretch of Bobbi's column, kept in place
  // as the diary scrolls.
  useEffect(() => {
    if (!run || step !== 'bookAsk') return undefined;
    let raf = null;
    let placed = false; // scrolled the free stretch into view yet
    const tick = () => {
      const el = targetRef.current;
      const col = document.querySelector('.dv2-staff-column--demo .dv2-column-body');
      const gap = getIntro().gap;
      if (el && col && gap) {
        // Measured from the column's own grid (its top is the diary's first
        // slot), so the box sits exactly on the hour and quarter lines and
        // is inset like a booking.
        const marks = hourMarks();
        const r = col.getBoundingClientRect();
        if (marks.length >= 2) {
          const a = marks[0];
          const b = marks[marks.length - 1];
          const perMin = (b.y - a.y) / (b.minutes - a.minutes);
          const viewStart = Math.round((a.minutes - (a.y - r.top) / perMin) / 15) * 15;
          const y = (m) => r.top + (m - viewStart) * perMin;
          el.style.left = `${r.left + 4}px`;
          el.style.width = `${Math.max(40, r.width - 8)}px`;
          el.style.top = `${y(gap.from) + 1}px`;
          el.style.height = `${Math.max(40, y(gap.to) - y(gap.from) - 2)}px`;
          el.style.opacity = '1';
          // The diary may have scrolled since the gap was picked (it follows
          // the time of day): bring the free stretch back up once.
          if (!placed) {
            placed = true;
            const head = document.querySelector('.dv2-staff-header');
            const headBottom = head ? head.getBoundingClientRect().bottom : 80;
            const top = y(gap.from);
            if (top < headBottom + 8 || top > window.innerHeight * 0.5) scrollDiary(col, top - (headBottom + 24));
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [run, step]);

  // Bailey wears the salon's saved look when there is one. Quietly: a new
  // salon has none, and the default Bailey is the right face for it.
  useEffect(() => {
    if (!run) return undefined;
    let alive = true;
    agentService.status()
      .then((res) => {
        const d = (res && res.data && res.data.responseData) || {};
        const saved = resolveAppearance(d);
        if (alive && saved) setLook(saved);
      })
      .catch(() => { /* default look */ });
    return () => { alive = false; };
  }, [run]);

  // Put the button under the thumb once the words have landed.
  useEffect(() => {
    if (ready && ctaRef.current) ctaRef.current.focus({ preventScroll: true });
  }, [ready, step]);

  if (!run) return null;

  const script = {
    meet: {
      title: `Hi${firstName ? ` ${firstName}` : ''}, I'm Bailey.`,
      body: `Your ${words.sidekick} sidekick. I'm here to help you run the place and grow it.`,
      cta: 'Hi Bailey',
      next: () => { hapticTick(); say('meet2', { state: 'attentive', activity: 'none' }); },
    },
    meet2: {
      title: "I'll help you get up to speed.",
      body: "Let's take a quick look round your new workspace. It only takes a minute.",
      cta: 'Show me around',
      next: flyToCorner,
    },
    team: {
      title: "This is your diary. It's a little empty.",
      body: 'First, let me add a couple of team members for you.',
      cta: 'Go on then',
      next: addTeam,
    },
    teamDone: {
      title: `Say hello to ${colleagueA} and ${colleagueB}.`,
      body: "They're just examples for now, so you can see how a team diary works.",
      cta: 'Nice',
      next: () => { hapticTick(); say('fill', { state: 'idle', activity: 'none' }); },
    },
    fill: {
      title: 'Things are looking a little plain.',
      body: 'Let me pop in some practice bookings, so you get a feel for how your diary will look.',
      cta: 'Fill it up',
      next: fillDiary,
    },
    custom: {
      title: "That's more like it.",
      body: 'Your diary is fully customisable. Prices, durations, processing times and deposits can all be set for each team member, and you can even change the colours to match your vibe.',
      chips: CUSTOMISE,
      cta: 'Next',
      next: () => { hapticTick(); say('paletteAsk', { state: 'needs-input', activity: 'none' }); },
    },
    paletteAsk: {
      title: 'Speaking of vibes…',
      body: 'Why not pick a palette now? You can change it any time.',
      cta: 'Choose a palette',
      next: openPalette,
      secondary: { label: 'Maybe later', go: () => { hapticTick(); setRetry(false); say('bookAsk', { state: 'complete', activity: 'none' }); } },
    },
    bookAsk: retry ? {
      title: 'No problem.',
      body: `Tap a free space in ${colleagueA}'s column whenever you're ready.`,
      hint: 'Tap the glowing space',
    } : {
      title: pickedPalette ? `Gorgeous. ${palette} suits you.` : 'Now for the good bit.',
      body: `Let's add a booking. Tap a free space in ${colleagueA}'s column.`,
      hint: 'Tap the glowing space',
    },
    bookClient: {
      title: "I've added some example clients for you.",
      body: 'Tap Search clients and pick one.',
    },
    bookService: {
      title: 'Lovely.',
      body: "I've added some example services too. Tap Add Service and choose one.",
    },
    bookSave: {
      title: 'Perfect. Now save the booking.',
      body: 'Tap Save Booking at the bottom.',
    },
    bookSaved: {
      title: 'Booked in!',
      body: "When you save a booking, Beu can email and text your client to confirm it. Don't worry, nothing is being sent right now. Your clients also get a lovely app to manage their bookings and rebook with you.",
      preview: true,
      cta: 'Next',
      next: () => { hapticTick(); setIntro({ labels: true }); say('mixTeam', { state: 'attentive', activity: 'none' }); },
    },
    mixTeam: {
      title: 'One of the things people love about Beu…',
      body: 'Teams with a mix of self-employed and employed people work together seamlessly. Let me show you.',
      cta: 'Show me',
      next: showSpot,
    },
    mixPick: spot ? {
      title: `${colleagueA} is self-employed.`,
      body: spot.ours
        ? `Let's check out ${possessive(spot.client)} ${spot.service}, the booking we just added.`
        : `Let's check out ${possessive(spot.client)} ${spot.service}.`,
      hint: 'Tap the booking',
    } : null,
    coSummary: {
      title: 'This is the checkout.',
      body: `Add more services or products here, and change how many of each. When you're ready, tap ${payLabel || `Pay${spot && spot.price ? ` £${spot.price.toFixed(2)}` : ''}`}. Don't worry, it's all demo data.`,
      hint: payLabel ? `Tap ${payLabel}` : 'Tap Pay',
    },
    coSplit: {
      title: "Here's how the money splits.",
      body: (() => {
        // The bill as the checkout has it (the Pay button), falling back to
        // the booking's price.
        const fromButton = parseFloat(String(payLabel).replace(/[^0-9.]/g, ''));
        const total = Number.isFinite(fromButton) && fromButton > 0 ? fromButton : (spot && spot.price) || 0;
        const salon = Math.round(total * SALON_SPLIT * 100) / 100;
        const stylist = Math.round((total - salon) * 100) / 100;
        const amounts = total
          ? ` On this ${pounds(total)} bill, ${pounds(stylist)} goes straight to ${colleagueA} and ${pounds(salon)} to you, split right at the till.`
          : ` Beu splits card payments right at the till, so ${colleagueA}'s share goes to ${colleagueA} and yours goes to you.`;
        return `${colleagueA} is self-employed and works on a ${Math.round(SALON_SPLIT * 100)}% split.${amounts} You can both see exactly where every pound went.`;
      })(),
      cta: 'Next',
      next: () => { hapticTick(); say('coReader', { state: 'attentive', activity: 'none' }); },
    },
    coReader: {
      title: 'Take cards with a Stripe reader.',
      body: 'It suggests a tip, splits the payment automatically, and each of you only processes your own share.',
      cta: 'Next',
      next: () => { hapticTick(); say('coPartial', { state: 'attentive', activity: 'none' }); },
    },
    coPartial: {
      title: 'Paying in parts?',
      body: 'Easy. Take some on card and the rest in cash, or any mix you like, with Custom amount. Beu keeps track of it all.',
      cta: 'Got it',
      next: () => {
        hapticSuccess();
        closingCheckoutRef.current = true;
        say('done', { state: 'complete', activity: 'none' });
        introCheckout('close');
      },
    },
    done: {
      title: "That's the tour.",
      body: 'Want me to help you set up the rest? We’ll take it one step at a time, and you can stop whenever you like.',
      cta: 'Yes, help me',
      next: () => finish('setup'),
      secondary: { label: 'Not now', go: () => finish('later') },
    },
  };

  const line = script[step];
  const staffFirst = booked
    ? (booked.staffId === 'demo-staff-1' ? colleagueA : booked.staffId === 'demo-staff-2' ? colleagueB : (firstName || 'us'))
    : '';
  const preview = booked ? {
    text: `Hi ${String(booked.clientName || '').split(' ')[0]}, you're booked in with ${staffFirst} on ${moment(booked.bookingDate, 'YYYY-MM-DD').format('ddd D MMM')} at ${hhmm(booked.startMinutes)}. See you then! ${salonName}`.trim(),
    service: (booked.services && booked.services[0] && booked.services[0].name) || '',
  } : null;
  const docked = DOCKED_STEPS.includes(step);
  const stageOn = step === 'meet' || step === 'meet2' || step === 'fly';
  const dotIndex = DOCKED_STEPS.indexOf(step);
  const travelling = pet === 'run-left' || pet === 'run-right';
  // Phone, booking panel open: Bailey rides along inside his card.
  const tucked = panelOpen && isPhone();

  return ReactDOM.createPortal(
    <div className="bi-root">
      <div className="bi-fx" ref={fxRef} aria-hidden="true" />

      {/* Centre stage: a frosted veil over the empty diary while Bailey says hello. */}
      {(stageOn || step === 'hero') && (
        <div className={`bi-stage${step === 'fly' ? ' is-leaving' : ''}${step === 'hero' ? ' is-under-hero' : ''}`} role={step === 'hero' ? undefined : 'dialog'} aria-modal={step === 'hero' ? undefined : 'true'} aria-label="Meet Bailey">
          {step !== 'hero' && <button type="button" className="bi-skip" onClick={finish}>Skip intro</button>}
        </div>
      )}

      {/* The ledge: a block the colour of the stage, so its top edge is
          invisible until Bailey climbs up over it. It sits above him, so
          whatever of him is below the edge stays hidden. */}
      {stageOn && (
        <div className={`bi-ledge${step === 'fly' ? ' is-leaving' : ''}`} ref={ledgeRef}>
          {(step === 'meet' || step === 'meet2') && line && !climbing && (
            <div className="bi-stage-say" key={step}>
              <Say title={line.title} body={line.body} instant={reducedMotion()} onDone={() => setReady(true)} />
              <div className={`bi-actions bi-actions--centre${ready ? ' is-in' : ''}`}>
                <button type="button" ref={ctaRef} className="bi-btn bi-btn--primary bi-btn--lg" onClick={line.next} disabled={!ready}>{line.cta}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bailey himself. One element for the whole intro, so the flight from
          centre stage to the corner is one continuous move. */}
      <div className={`bi-pet is-${pet}${tucked ? ' is-tucked' : ''}`} ref={petRef} aria-hidden={pet === 'hidden'}>
        <div className="bi-pet-y">
          <div className="bi-pet-body">
            {/* Mounted from the start, below the floor, so the 3D model has
                loaded by the time he climbs into view. */}
            <BaileyAvatar appearance={look} state={bailey.state} activity={bailey.activity} />
          </div>
        </div>
      </div>

      {/* The spotlit booking's "tap" chip. Clicks pass straight through. */}
      {step === 'mixPick' && spot && (
        <div className="bi-spot-chip" ref={chipRef} aria-hidden="true">Tap to open</div>
      )}

      {/* Where to tap for the practice booking. Clicks pass straight through. */}
      {step === 'bookAsk' && (
        <div className="bi-target" ref={targetRef} aria-hidden="true">
          <span className="bi-target-label">Tap here</span>
        </div>
      )}

      {/* Docked: Bailey talks from the corner while the diary changes. */}
      {docked && !paletteOpen && line && (
        <div className={`bi-bubble${tucked ? ' is-compact' : ''}${busy ? ' is-busy' : ''}${pet === 'left' || pet === 'run-left' ? ' is-left' : ''}${travelling ? ' is-travelling' : ''}`} ref={panelRef} key={`b-${step}-${travelling ? 't' : 's'}`}>
          <div className="bi-bubble-head">
            {tucked && <span className="bi-bubble-face" aria-hidden="true"><BaileyStill appearance={look} /></span>}
            <span className="bi-bubble-name">Bailey</span>
            <span className="bi-dots" aria-label={`Step ${dotIndex + 1} of ${DOCKED_STEPS.length}`}>
              {DOCKED_STEPS.map((s, i) => <span key={s} className={`${i <= dotIndex ? 'is-on' : ''}${i === dotIndex ? ' is-now' : ''}`} />)}
            </span>
            <button type="button" className="bi-skip bi-skip--inline" onClick={finish}>Skip</button>
          </div>
          {busy ? (
            <div className="bi-working" aria-live="polite">
              <span className="bi-working-dots"><i /><i /><i /></span>
              {finishing ? 'Tidying up…' : step === 'team' ? 'Adding your team…' : 'Filling your diary…'}
            </div>
          ) : (
            <>
              <Say title={line.title} body={line.body} instant={reducedMotion()} onDone={() => setReady(true)} />
              {line.chips && (
                <div className={`bi-chips${ready ? ' is-in' : ''}`}>
                  {line.chips.map((c, i) => <span key={c} className="bi-chip" style={{ transitionDelay: `${i * 55}ms` }}>{c}</span>)}
                </div>
              )}
              {line.preview && preview && (
                <div className={`bi-preview${ready ? ' is-in' : ''}`}>
                  <div className="bi-preview-msg">
                    <span className="bi-preview-from">Text to your client</span>
                    <p>{preview.text}</p>
                  </div>
                  <div className="bi-preview-chips">
                    <span className="bi-preview-chip"><i className="bi-dot bi-dot--teal" />Confirmation email</span>
                    <span className="bi-preview-chip"><i className="bi-dot bi-dot--violet" />Text message</span>
                    <span className="bi-preview-chip"><i className="bi-dot bi-dot--peach" />Client app</span>
                  </div>
                </div>
              )}
              {line.hint && (
                <div className={`bi-hint${ready ? ' is-in' : ''}`}>
                  <span className="bi-hint-dot" aria-hidden="true" />{line.hint}
                </div>
              )}
              {(line.cta || line.secondary) && (
                <div className={`bi-actions${ready ? ' is-in' : ''}`}>
                  {line.secondary && <button type="button" className="bi-btn bi-btn--ghost" onClick={line.secondary.go} disabled={!ready}>{line.secondary.label}</button>}
                  {line.cta && <button type="button" ref={ctaRef} className="bi-btn bi-btn--primary" onClick={line.next} disabled={!ready}>{line.cta}</button>}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {paletteOpen && <PaletteSheet current={palette} onPick={pickPalette} onDone={paletteDone} sheetRef={panelRef} />}

      {step === 'hero' && (
        <Hero firstName={firstName} salonName={salonName} trade={trade} leaving={heroLeaving} onGo={heroGo} onSkip={finish} />
      )}
    </div>,
    document.body,
  );
}
